import * as Dialog from '@radix-ui/react-dialog';
import * as Popover from '@radix-ui/react-popover';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Pencil, Plus, Trash2, Loader2 } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { updateUser, updatePassword, connectTelegram } from 'api/authApi';
import { updateUserData, logout } from 'store/slices/authSlice';
import { toast } from 'react-hot-toast';
import { fileToUrl } from 'api/commonApi';
import { LoginButton } from '@telegram-auth/react';

interface AccountSettingsDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const AccountSettingsDialog = ({
  isOpen,
  onClose,
}: AccountSettingsDialogProps) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);

  // State for form fields
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.phone || '');

  // Edit states
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [isEditingPhone, setIsEditingPhone] = useState(false);

  // Add state for password form
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    old_password: '',
    new_password1: '',
    new_password2: '',
  });

  // Add these new states
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  // Add loading states
  const [isUpdatingName, setIsUpdatingName] = useState(false);
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  const [isUpdatingPhone, setIsUpdatingPhone] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [isUpdatingPhoto, setIsUpdatingPhoto] = useState(false);
  const [isRemovingPhoto, setIsRemovingPhoto] = useState(false);

  // Sync local state with user data when it changes
  useEffect(() => {
    if (user) {
      setDisplayName(user.displayName || '');
      setEmail(user.email || '');
      setPhone(user.phone || '');
    }
  }, [user]);

  // Handle display name update
  const handleUpdateDisplayName = async () => {
    try {
      setIsUpdatingName(true);
      await updateUser({ display_name: displayName });
      dispatch(updateUserData({ displayName }));
      setIsEditingName(false);
      toast.success('Display name updated successfully');
    } catch (error) {
      console.error('Error updating display name:', error);
      toast.error('Failed to update display name');
    } finally {
      setIsUpdatingName(false);
    }
  };

  // Handle email update
  const handleUpdateEmail = async () => {
    try {
      setIsUpdatingEmail(true);
      if (!email) {
        toast.error('Please enter a valid email');
        return;
      }

      await updateUser({ email });
      dispatch(updateUserData({ email }));
      // Update localStorage
      const storedUser = localStorage.getItem('authUser');
      if (storedUser) {
        const updatedUser = { ...JSON.parse(storedUser), email };
        localStorage.setItem('authUser', JSON.stringify(updatedUser));
      }
      setIsEditingEmail(false);
      toast.success('Email updated successfully');
    } catch (error: any) {
      console.error('Error updating email:', error);
      const errorMessage =
        error.response?.data?.message || 'Failed to update email';
      toast.error(errorMessage);
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  // Handle phone update
  const handleUpdatePhone = async () => {
    try {
      setIsUpdatingPhone(true);
      if (!phone) {
        toast.error('Please enter a valid phone number');
        return;
      }

      await updateUser({ phone });
      dispatch(updateUserData({ phone }));
      // Update localStorage
      const storedUser = localStorage.getItem('authUser');
      if (storedUser) {
        const updatedUser = { ...JSON.parse(storedUser), phone };
        localStorage.setItem('authUser', JSON.stringify(updatedUser));
      }
      setIsEditingPhone(false);
      toast.success('Phone number updated successfully');
    } catch (error: any) {
      console.error('Error updating phone:', error);
      const errorMessage =
        error.response?.data?.message || 'Failed to update phone number';
      toast.error(errorMessage);
    } finally {
      setIsUpdatingPhone(false);
    }
  };

  // Handle password form changes
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordForm({
      ...passwordForm,
      [e.target.name]: e.target.value,
    });
  };

  // Handle password update
  const handleUpdatePassword = async () => {
    try {
      setIsUpdatingPassword(true);
      // Basic validation
      if (!passwordForm.old_password) {
        toast.error('Please enter your current password');
        return;
      }
      if (!passwordForm.new_password1) {
        toast.error('Please enter a new password');
        return;
      }
      if (passwordForm.new_password1 !== passwordForm.new_password2) {
        toast.error('New passwords do not match');
        return;
      }
      if (passwordForm.new_password1.length < 8) {
        toast.error('Password must be at least 8 characters long');
        return;
      }

      await updatePassword(passwordForm);
      toast.success('Password updated successfully');
      setPasswordForm({
        old_password: '',
        new_password1: '',
        new_password2: '',
      });
      setIsChangingPassword(false);
    } catch (error: any) {
      console.error('Error updating password:', error);
      const errorMessage =
        error.response?.data?.message || 'Failed to update password';
      toast.error(errorMessage);
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      try {
        setIsUpdatingPhoto(true);
        const MAX_FILE_SIZE_MB = 0.35; // 350KB
        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
          toast.error('File exceeds the 350KB size limit');
          return;
        }

        const fileUrlResponse = await fileToUrl(file);

        if (!fileUrlResponse.data?.url) {
          throw new Error('Failed to get file URL');
        }

        // Update user with the new image URL
        const updateResponse = await updateUser({
          img_url: fileUrlResponse.data.url,
        });
        console.log('Update User Response:', updateResponse); // Debug log

        dispatch(
          updateUserData({ imgUrl: updateResponse.data.user.img_url })
        );
        toast.success('Profile picture updated successfully');
      } catch (error: any) {
        console.error('Error in handleFileChange:', error);
        const errorMessage =
          error.message || 'Failed to update profile picture';
        toast.error(errorMessage);
      } finally {
        setIsUpdatingPhoto(false);
      }
    }
  };

  const handleChangePhoto = () => {
    // Create an input element
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) =>
      handleFileChange(e as unknown as React.ChangeEvent<HTMLInputElement>);
    // Add a title attribute to show file size limit
    input.title = 'Maximum file size: 350KB';
    input.click();
  };

  const handleRemovePhoto = async () => {
    try {
      setIsRemovingPhoto(true);
      await updateUser({ img_url: '' });
      dispatch(updateUserData({ imgUrl: '' }));
      toast.success('Profile picture removed successfully');
    } catch (error: any) {
      console.error('Error removing profile picture:', error);
      const errorMessage = error.response?.data?.message || 'Failed to remove profile picture';
      toast.error(errorMessage);
    } finally {
      setIsRemovingPhoto(false);
    }
  };

  // Add this new handler
  const handleDeleteAccount = async () => {
    try {
      if (deleteConfirmation !== 'CONFIRM') {
        toast.error('Please type CONFIRM to delete your account');
        return;
      }

      await updateUser({ is_deleted: true });
      dispatch(logout());
      onClose();
      toast.success('Your account has been deleted');
    } catch (error: any) {
      console.error('Error deleting account:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete account';
      toast.error(errorMessage);
    }
  };

  // Handle Google Connect
  const handleGoogleConnect = React.useCallback(async () => {
    const redirect_uri = `${window.location.origin}/auth/connect-google-callback`;
    try {
      window.location.href = `https://accounts.google.com/o/oauth2/auth?client_id=${process.env.REACT_APP_GOOGLE_CLIENT_ID}&redirect_uri=${redirect_uri}&response_type=code&scope=openid email profile`;
      toast.success('Redirecting to Google...');
    } catch (error) {
      toast.error('Failed to connect with Google');
      console.error(error);
    }
  }, []);

  // Handle Telegram Connect
  const handleTelegramConnect = async (response: any) => {
    try {
      await connectTelegram(response);
      toast.success('Successfully connected with Telegram');
    } catch (error) {
      toast.error('Failed to connect with Telegram');
      console.error(error);
    }
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-[100]" />
        <Dialog.Content className="text-text-primary fixed left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] bg-background rounded-xl w-[560px] max-h-[85vh] overflow-y-auto z-[101]">
          <div className="p-6">
            <Tabs
              defaultValue="account"
              className="w-full"
              // onValueChange={(value) => setActiveTab(value)}
            >
              <TabsList className="mb-2 flex w-full bg-transparent space-x-6 border-0 p-0">
                <TabsTrigger
                  value="account"
                  className="text-sm bg-transparent border-0 px-0 py-2 rounded-b-none font-medium data-[state=active]:bg-transparent data-[state=active]:text-[#7857FF] data-[state=active]:border-b-2 data-[state=active]:border-[#7857FF] data-[state=active]:shadow-none"
                >
                  ACCOUNT SETTINGS
                </TabsTrigger>
                {/* <TabsTrigger 
                  value="connected"
                  className="text-sm bg-transparent border-0 px-0 py-2 font-medium text-gray-500 data-[state=active]:bg-transparent data-[state=active]:text-[#7857FF] data-[state=active]:border-b-2 data-[state=active]:border-[#7857FF] data-[state=active]:shadow-none"
                >
                  CONNECTED ACCOUNTS
                </TabsTrigger> */}
              </TabsList>

              <div className="h-[1px] w-full bg-gray-200 mb-8"></div>

              {/* Profile Picture */}
              <div className="flex justify-center mb-8">
                <div className="relative">
                  <div className="w-20 h-20 rounded-full overflow-hidden flex items-center justify-center text-white text-2xl">
                    {user?.imgUrl ? (
                      <img
                        src={user.imgUrl}
                        alt={user.displayName}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.parentElement!.style.backgroundColor =
                            '#7857FF';
                          e.currentTarget.parentElement!.innerHTML =
                            user.displayName?.charAt(0).toUpperCase() || '';
                        }}
                      />
                    ) : (
                      user?.displayName?.charAt(0).toUpperCase()
                    )}
                  </div>

                  <Popover.Root modal={true}>
                    <Popover.Trigger asChild>
                      <button 
                        className="absolute bottom-0 right-0 bg-white rounded-full p-1.5 shadow-md hover:bg-gray-50"
                      >
                        <Pencil className="h-4 w-4 text-gray-600" />
                      </button>
                    </Popover.Trigger>

                    <Popover.Portal>
                      <Popover.Content
                        className="bg-white rounded-md shadow-[0_0_7px_rgba(0,0,0,0.1)] p-0 w-[140px] z-[999] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
                        sideOffset={5}
                        align="center"
                      >
                        <div className="flex flex-col">
                          <button
                            onClick={handleRemovePhoto}
                            disabled={isRemovingPhoto}
                            className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-100"
                          >
                            {isRemovingPhoto ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4 text-gray-500" />
                            )}
                            Remove photo
                          </button>
                          <button
                            onClick={handleChangePhoto}
                            disabled={isUpdatingPhoto}
                            className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                          >
                            {isUpdatingPhoto ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Plus className="h-4 w-4 text-gray-500" />
                            )}
                            Change photo
                          </button>
                        </div>
                      </Popover.Content>
                    </Popover.Portal>
                  </Popover.Root>
                </div>
              </div>

              <TabsContent
                value="account"
                className="space-y-6 text-text-primary"
              >
                {/* Display Name */}
                <div>
                  <label className="block text-sm mb-2">Display Name</label>
                  <div className="flex gap-3">
                    <Input
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      className="flex-1"
                      disabled={!isEditingName}
                    />
                    <Button 
                      variant="secondary" 
                      className="w-[150px] flex-shrink-0 text-primary hover:bg-[#F4F3FF]/90 text-base"
                      onClick={() => {
                        if (isEditingName) {
                          handleUpdateDisplayName();
                        } else {
                          setIsEditingName(true);
                        }
                      }}
                      disabled={isUpdatingName}
                    >
                      {isUpdatingName ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : isEditingName ? 'Save' : 'Edit'}
                    </Button>
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm mb-2">Email</label>
                  <div className="flex gap-3">
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={user?.email ? '' : 'Add email'}
                      className="flex-1"
                      disabled={!isEditingEmail}
                    />
                    <Button 
                      variant="secondary" 
                      className="w-[150px] flex-shrink-0 text-primary hover:bg-[#F4F3FF]/90 text-base"
                      onClick={() => {
                        if (isEditingEmail) {
                          handleUpdateEmail();
                        } else {
                          setIsEditingEmail(true);
                        }
                      }}
                      disabled={isUpdatingEmail}
                    >
                      {isUpdatingEmail ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : isEditingEmail ? 'Save' : user?.email ? 'Edit' : 'Add'}
                    </Button>
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm mb-2">Phone number</label>
                  <div className="flex gap-3">
                    <Input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder={user?.phone ? '' : 'Add phone number'}
                      className="flex-1"
                      disabled={!isEditingPhone}
                    />
                    <Button 
                      variant="secondary" 
                      className="w-[150px] flex-shrink-0 text-primary hover:bg-[#F4F3FF]/90 text-base"
                      onClick={() => {
                        if (isEditingPhone) {
                          handleUpdatePhone();
                        } else {
                          setIsEditingPhone(true);
                        }
                      }}
                      disabled={isUpdatingPhone}
                    >
                      {isUpdatingPhone ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : isEditingPhone ? 'Save' : user?.phone ? 'Edit' : 'Add'}
                    </Button>
                  </div>
                </div>

                {/* Password */}
                {!isChangingPassword ? (
                  <div>
                    <Button 
                      variant="link" 
                      className="text-primary underline p-0 text-base"
                      onClick={() => setIsChangingPassword(true)}
                    >
                      Change password
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Input
                      type="password"
                      name="old_password"
                      placeholder="Current password"
                      value={passwordForm.old_password}
                      onChange={handlePasswordChange}
                      className="w-full"
                    />
                    <Input
                      type="password"
                      name="new_password1"
                      placeholder="New password"
                      value={passwordForm.new_password1}
                      onChange={handlePasswordChange}
                      className="w-full"
                    />
                    <Input
                      type="password"
                      name="new_password2"
                      placeholder="Confirm new password"
                      value={passwordForm.new_password2}
                      onChange={handlePasswordChange}
                      className="w-full"
                    />
                    <div className="flex gap-3">
                      <Button 
                        variant="secondary" 
                        className="w-[150px] flex-shrink-0 text-primary hover:bg-[#F4F3FF]/90 text-base"
                        onClick={handleUpdatePassword}
                        disabled={isUpdatingPassword}
                      >
                        {isUpdatingPassword ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : 'Save'}
                      </Button>
                      <Button 
                        variant="secondary" 
                        className="w-[150px] flex-shrink-0 text-gray-500 hover:bg-gray-50 text-base"
                        onClick={() => {
                          setIsChangingPassword(false);
                          setPasswordForm({
                            old_password: '',
                            new_password1: '',
                            new_password2: '',
                          });
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {/* Delete Account */}
                <div className="pt-4">
                  <Button 
                    variant="link" 
                    className="text-primary p-0 underline text-base"
                    onClick={() => setIsDeleteDialogOpen(true)}
                  >
                    Delete account
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="connected" className="space-y-4">
                {/* Connected Accounts Content */}
                <div className="space-y-4">
                  {/* Google */}
                  <div className="flex justify-between items-center p-4 bg-white rounded-lg shadow-sm">
                    <div>
                      <h4 className="font-medium">Google</h4>
                      <p className="text-sm text-gray-500">Connect your Google account</p>
                    </div>
                    <button
                      onClick={handleGoogleConnect}
                      className="bg-[#F4F3FF] px-4 py-2 rounded-md text-[#7857FF] hover:bg-[#F4F3FF]/80"
                    >
                      Connect
                    </button>
                  </div>

                  {/* Telegram */}
                  <div className="flex justify-between items-center p-4 bg-white rounded-lg shadow-sm">
                    <div>
                      <h4 className="font-medium">Telegram</h4>
                      <p className="text-sm text-gray-500">Connect your Telegram account</p>
                    </div>
                    <LoginButton
                      botUsername={process.env.REACT_APP_TELEGRAM_BOT_USERNAME}
                      buttonSize="medium"
                      cornerRadius={8}
                      onAuthCallback={handleTelegramConnect}
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </Dialog.Content>
      </Dialog.Portal>

      {/* Add the Delete Account Dialog */}
      <Dialog.Root open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50 z-[200]" />
          <Dialog.Content className="fixed text-text-primary left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] bg-background rounded-xl w-[400px] p-6 z-[201]">
            <div className="space-y-4">
              <Dialog.Title className="text-lg font-semibold">Delete Account</Dialog.Title>
              <p className="text-sm">
                This action cannot be undone. This will permanently delete your account and remove all your data.
              </p>
              <p className="text-sm">
                Please type <span className="font-semibold">CONFIRM</span> to delete your account:
              </p>
              <Input
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder="Type CONFIRM"
                className="w-full"
              />
              <div className="flex gap-3 pt-2">
                <Button 
                  variant="customSecondary"
                  className="w-full text-sm"
                  onClick={handleDeleteAccount}
                >
                  Delete Account
                </Button>
                <Button 
                  variant="customPrimary"
                  className="w-full text-sm"
                  onClick={() => {
                    setIsDeleteDialogOpen(false);
                    setDeleteConfirmation('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </Dialog.Root>
  );
};

export default AccountSettingsDialog;
